import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { SeasonalPill } from "../components/SeasonalPill.jsx";
import { SeasonBanner } from "../components/SeasonBanner.jsx";
import { currentSeason, seasonalProducts } from "../config/seasons.js";
import { CategoryArtTile } from "../components/CategoryArt.jsx";
import { HeroCarousel } from "../components/HeroCarousel.jsx";
import { trackAdd } from "../utils/trackCart.js";
import { useNavigate, useLocation } from "react-router-dom";
import { BACKEND_URL, supabaseClient } from "../config/supabase.js";
import { useIsMobile } from "../hooks/useIsMobile.js";
import { Footer } from "../components/Footer.jsx";
import { thumbUrl } from "../utils/imageUpload.js";
import { TAXONOMY } from "../config/taxonomy.js";
import { RestockRequestModal } from "../components/RestockRequestModal.jsx";
import { ProductTile, productImage, isSoldOut, needsChoice, quickChoiceOnly } from "../components/ProductTile.jsx";
import { QuickAddSheet } from "../components/QuickAddSheet.jsx";
import { PromoTiles, buildPromos } from "../components/PromoTiles.jsx";
import { buildHeldMap, applyHolds, bogoLabel } from "../utils/helpers.js";
import { setVisibleInterval } from "../utils/visibleInterval.js";
import { Icon, Monogram } from "../components/Icon.jsx";

// ─── DISCOVER (marketplace home) ─────────────────────────────────────────────
// Light "Market" layout from the redesign: category sidebar + featured banner + account cards, a trust strip, product shelves,
// a Store-of-the-week feature, "Stores we like", and the sell band. All content comes from the same marketplace endpoints
// as before — nothing here is invented (no ratings, no fake stock).

// A single-line bar of links that scrolls sideways (hidden scrollbar) with arrow buttons that appear when there is more to reach.
function ScrollBar({ children, pinnedRight, isMobile }) {
  const ref = useRef(null);
  const [edge, setEdge] = useState({ left: false, right: false });
  const measure = useCallback(() => {
    const el = ref.current; if (!el) return;
    setEdge({ left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
  }, []);
  useEffect(() => { measure(); window.addEventListener("resize", measure); return () => window.removeEventListener("resize", measure); }, [measure, children]);
  const nudge = (dir) => ref.current?.scrollBy({ left: dir * 320, behavior: "smooth" });
  const arrow = (dir, show) => show && !isMobile && (
    <button type="button" aria-label={dir < 0 ? "Scroll left" : "Scroll right"} onClick={() => nudge(dir)}
      style={{ flexShrink: 0, width: 28, height: 28, border: "1px solid var(--border-mid)", borderRadius: 4, background: "var(--white)", color: "var(--ink)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
      <Icon n={dir < 0 ? "chevron-left" : "chevron-right"} size={16} />
    </button>
  );
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, height: 44 }}>
      {arrow(-1, edge.left)}
      <div ref={ref} onScroll={measure} style={{ flex: 1, minWidth: 0, height: 44, display: "flex", alignItems: "center", gap: isMobile ? 20 : 22, overflowX: "auto", scrollbarWidth: "none" }}>{children}</div>
      {arrow(1, edge.right)}
      {pinnedRight}
    </div>
  );
}

const barLink = (strong) => ({ background:"none", border:"none", fontFamily:"inherit", fontSize:14, fontWeight:strong?600:500, cursor:"pointer", height:44, padding:0, color:strong?"var(--ink)":"var(--text2)", whiteSpace:"nowrap", flexShrink:0 });

const SectionPanel = ({ children, style }) => (
  <section style={{ background: "var(--white)", border: "1px solid var(--border)", borderRadius: 4, ...style }}>{children}</section>
);

const SeeAll = ({ onClick, label = "See all" }) => (
  <button onClick={onClick} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 14, fontWeight: 600, color: "var(--teal)", display: "inline-flex", alignItems: "center", gap: 4, padding: 0 }}>
    {label}<Icon n="chevron-right" size={16} />
  </button>
);

// Horizontal-scroll row on phones, 6-up grid on desktop.
function Shelf({ title, products, isMobile, onSeeAll, onOpen, onAdd, addedIds }) {
  if (!products || products.length < 2) return null;
  const list = products.slice(0, isMobile ? 12 : 6);
  return (
    <SectionPanel style={{ padding: isMobile ? "40px 0 48px" : "24px 24px 30px", borderLeft: isMobile ? "none" : undefined, borderRight: isMobile ? "none" : undefined, borderRadius: isMobile ? 0 : 4 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: isMobile ? "0 16px 24px" : "0 0 20px" }}>
        <h2 className="serif" style={{ fontSize: isMobile ? 32 : 40, margin: 0 }}>{title}</h2>
        <SeeAll onClick={onSeeAll} />
      </div>
      {isMobile ? (
        <div style={{ display: "flex", gap: 14, overflowX: "auto", padding: "0 16px", scrollbarWidth: "none" }}>
          {list.map(p => <ProductTile key={p.id} product={p} fixedWidth={146} onOpen={onOpen} onAdd={onAdd} added={addedIds.has(p.id)} />)}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 14 }}>
          {list.map(p => <ProductTile key={p.id} product={p} onOpen={onOpen} onAdd={onAdd} added={addedIds.has(p.id)} />)}
        </div>
      )}
    </SectionPanel>
  );
}

// "Shop by category" — the one visual category section on the home page (desktop). A 2x2 collage of real product photos
// when the category has four in the loaded pool, otherwise an icon tile, so every category is always reachable.
const TINTS = ["#FFF1BF", "#DCEBFA", "#FFE3D3", "#F0EDE8"];
function CategoryTiles({ allProducts, navigate }) {
  const tiles = useMemo(() => {
    const byCat = new Map();
    for (const p of allProducts) {
      const cat = p.taxonomy_cat; if (!cat) continue;
      const imgs = Array.isArray(p.images) ? p.images.filter(Boolean) : [];
      const img = imgs[1] || imgs[0] || p.img; if (!img) continue;   // a later image is usually a product-only shot
      if (!byCat.has(cat)) byCat.set(cat, []);
      const arr = byCat.get(cat); if (arr.length < 4) arr.push(img);
    }
    return TAXONOMY.slice(0, 8).map(c => ({ slug: c.slug, label: c.label, icon: c.icon, imgs: byCat.get(c.slug) || [] }));
  }, [allProducts]);
  return (
    <SectionPanel style={{ padding: "24px 24px 28px", borderRadius: 4 }}>
      <h2 className="serif" style={{ fontSize: 44, margin: "0 0 20px" }}>Shop by category</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(8, minmax(0, 1fr))", gap: 14 }}>
        {tiles.map(t => (
          <button key={t.slug} onClick={() => navigate(`/category/${t.slug}`)}
            style={{ background: "var(--white)", border: "1px solid var(--border)", borderRadius: 4, overflow: "hidden", cursor: "pointer", padding: 0, fontFamily: "inherit", textAlign: "left" }}>
            {t.imgs.length >= 4 ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr", gap: 2, aspectRatio: "1 / 1", background: "var(--surface)" }}>
                {t.imgs.slice(0, 4).map((im, i) => (
                  <div key={i} style={{ overflow: "hidden", background: "var(--surface)" }}><img src={thumbUrl(im, 240)} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} /></div>
                ))}
              </div>
            ) : (
              <CategoryArtTile slug={t.slug} icon={t.icon} iconSize={56} strokeWidth={1.1} style={{ aspectRatio: "1 / 1" }} />
            )}
            <div style={{ padding: "10px 12px", borderTop: "1px solid var(--border)", fontSize: 14, fontWeight: 600, lineHeight: 1.25, minHeight: 56, display: "flex", alignItems: "center" }}>{t.label}</div>
          </button>
        ))}
      </div>
    </SectionPanel>
  );
}

// ─── STORES GALLERY ──────────────────────────────────────────────────────────
// "Browse stores" / "All stores": its own full panel (not a pop-up). Every store gets a card with a photo mosaic of its real
// products, its name, product count and its real perks (free shipping, Buy X Get Y). Search narrows the list.
const MOSAIC_TINTS = ["#FFF1BF", "#DCEBFA", "#FFE3D3", "#F0EDE8"];
function StoresGallery({ stores, productsOf, onBack, navigate, isMobile }) {
  const [q, setQ] = useState("");
  const cards = useMemo(() => stores.map(s => {
    const prods = productsOf(s).filter(p => productImage(p));
    return { s, imgs: prods.slice(0, 3).map(productImage), n: s._count ?? prods.length };
  }).sort((a, b) => b.n - a.n || (a.s.store_name || "").localeCompare(b.s.store_name || "")), [stores, productsOf]);
  const list = q.trim() ? cards.filter(c => (c.s.store_name || "").toLowerCase().includes(q.trim().toLowerCase())) : cards;
  const cell = (im, i, extra) => im
    ? <div key={i} style={{ overflow: "hidden", background: "var(--surface)", ...extra }}><img src={thumbUrl(im, 500)} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} /></div>
    : <div key={i} style={{ background: MOSAIC_TINTS[i % MOSAIC_TINTS.length], ...extra }} />;
  return (
    <div style={{ padding: isMobile ? "16px 12px 0" : 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <button onClick={onBack} className="btn-ghost" style={{ height: 40, padding: "0 14px", fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6 }}><Icon n="chevron-left" size={16} />Back</button>
        <h1 className="serif" style={{ fontSize: isMobile ? 36 : 52, margin: 0 }}>All stores <span style={{ color: "var(--ink-soft)", fontWeight: 400, fontSize: isMobile ? 15 : 18 }}>({list.length})</span></h1>
        <div style={{ marginLeft: isMobile ? 0 : "auto", width: isMobile ? "100%" : 340, display: "flex", alignItems: "center", gap: 10, border: "1px solid #948C80", borderRadius: 4, padding: "0 12px", height: 44, background: "var(--white)" }}>
          <Icon n="search" size={18} color="var(--ink-soft)" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search stores" aria-label="Search stores"
            style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontSize: 15, color: "var(--ink)", fontFamily: "inherit" }} />
          {q && <button onClick={() => setQ("")} aria-label="Clear" style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", color: "var(--ink-soft)" }}><Icon n="close" size={16} /></button>}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(auto-fill, minmax(360px, 1fr))", gap: isMobile ? 12 : 16 }}>
        {list.map(({ s, imgs, n }) => {
          const offer = bogoLabel(s);
          const perks = [Number(s.shipping_cost) === 0 && "Free shipping", offer, s.has_upi && "UPI"].filter(Boolean);
          return (
            <article key={s.id} onClick={() => navigate(`/store/${s.store_slug}`)}
              style={{ background: "var(--white)", border: "1px solid var(--border)", borderRadius: 4, overflow: "hidden", cursor: "pointer", display: "flex", flexDirection: "column" }}>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gridTemplateRows: "1fr 1fr", gap: 2, height: isMobile ? 190 : 230, background: "var(--surface)" }}>
                {cell(imgs[0], 0, { gridRow: "1 / span 2" })}
                {cell(imgs[1], 1)}
                {cell(imgs[2], 2)}
              </div>
              <div style={{ padding: "14px 16px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {s.profile_image
                    ? <img src={thumbUrl(s.profile_image, 120)} alt="" style={{ width: 44, height: 44, borderRadius: 4, objectFit: "cover", flexShrink: 0 }} />
                    : <Monogram name={s.store_name} size={44} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 17, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{s.store_name}</div>
                    <div style={{ fontSize: 13, color: "var(--ink-soft)", marginTop: 2 }}>{n} {n === 1 ? "product" : "products"}</div>
                  </div>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: 14, fontWeight: 600, color: "var(--teal)", whiteSpace: "nowrap" }}>Visit<Icon n="chevron-right" size={18} /></span>
                </div>
                {perks.length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {perks.map(t => <span key={t} style={{ fontSize: 12, fontWeight: 600, background: t === offer ? "#FFF1BF" : "var(--surface)", color: "var(--text2)", borderRadius: 2, padding: "2px 8px" }}>{t}</span>)}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
      {list.length === 0 && <div style={{ textAlign: "center", padding: "60px 0", color: "var(--ink-soft)", fontSize: 14 }}>No stores found for "{q}"</div>}
    </div>
  );
}

// ─── MARKETPLACE ─────────────────────────────────────────────────────────────
// "Pick up where you left off" — only when the cart has something in it: up to four thumbnails (tap → that product,
// × removes it) and a View cart button. (This box existed on the original home page; it came back here after the redesign.)
function ContinueCart({ cart, setCart, allProducts, navigate }) {
  if (!cart || cart.length === 0) return null;
  const items = cart.slice(0, 4);
  const count = cart.reduce((n, c) => n + (Number(c.qty) || 1), 0);
  return (
    <div style={{ background: "var(--white)", border: "1px solid var(--border)", borderRadius: 4, padding: 16 }}>
      <div style={{ fontSize: 15, fontWeight: 700 }}>Pick up where you left off</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8, marginTop: 12 }}>
        {items.map(ci => {
          const img = Array.isArray(ci.images) ? ci.images[0] : ci.img;
          const key = ci.cartKey || ci.id;
          return (
            <div key={key} style={{ position: "relative", aspectRatio: "1 / 1", borderRadius: 4, overflow: "hidden", background: "var(--surface)", border: "1px solid var(--border)", cursor: "pointer" }}
              onClick={() => { const fp = allProducts.find(p => p.id === ci.id); navigate(fp ? `/product/${fp.id}` : "/cart"); }}>
              {img ? <img src={thumbUrl(img, 200)} alt={ci.name || ""} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} /> : null}
              <button type="button" aria-label={`Remove ${ci.name || "item"}`} onClick={e => { e.stopPropagation(); setCart(prev => prev.filter(x => (x.cartKey || x.id) !== key)); }}
                style={{ position: "absolute", top: 3, right: 3, width: 20, height: 20, borderRadius: 4, border: "none", background: "rgba(27,24,20,0.7)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0 }}><Icon n="close" size={12} /></button>
            </div>
          );
        })}
      </div>
      <button className="btn-pink" onClick={() => navigate("/cart")} style={{ width: "100%", marginTop: 12, height: 42, padding: 0, fontSize: 14 }}>
        View cart · {count} {count === 1 ? "item" : "items"}{cart.length > 4 ? ` (+${cart.length - 4} more lines)` : ""}
      </button>
    </div>
  );
}

export function Marketplace({ cart, setCart, customer, setPage, goToDemo, paymentsPaused = false }) {
  const isMobile  = useIsMobile();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [allProducts,    setAllProducts]    = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [viewMode,       setViewMode]       = useState("rows");
  const [gridProducts,   setGridProducts]   = useState([]);
  const [gridLoading,    setGridLoading]    = useState(false);
  const [gridTitle,      setGridTitle]      = useState("All products");
  const [gridOverride,   setGridOverride]   = useState(null); // if set, show these products instead of gridProducts
  const [toast,          setToast]          = useState(null); // { name, img }
  const [restockProduct, setRestockProduct] = useState(null); // product to show restock modal for
  const toastTimer = useRef(null);
  const [heldMap, setHeldMap] = useState(new Map());
  // Show "In bag" badge for all products already in the cart
  const addedIds = useMemo(() => new Set(cart.map(i => i.id)), [cart]);

  // ── Discovery sections — each from a dedicated backend query ───────────────────
  // popular = most ordered products, newArrivals = newest by created_at,
  // staffPicks = top-tier stores, justForYou = mid-tier stores
  const [sections, setSections] = useState({ popular:[], newArrivals:[], staffPicks:[], justForYou:[], explore:[] });
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/marketplace/sections`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setSections(d); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${BACKEND_URL}/api/marketplace/products?limit=160&sort=newest`);
        const d = r.ok ? await r.json() : { products:[] };
        setAllProducts(d.products || []);
      } catch(e) { console.error("Marketplace load:", e); }
      setLoading(false);
    })();
  }, []);

  // ── Realtime stock updates for marketplace ───────────────────────────────
  // When any product's stock changes in DB, update it across all sections
  // so Sold Out shows instantly without refresh.
  useEffect(() => {
    const updateStock = (payload) => {
      const { id, stock, variations } = payload.new;
      const patch = p => p.id === id ? { ...p, stock, variations } : p;
      setSections(prev => ({
        popular:     prev.popular.map(patch),
        newArrivals: prev.newArrivals.map(patch),
        staffPicks:  prev.staffPicks.map(patch),
        justForYou:  prev.justForYou.map(patch),
        explore:     (prev.explore || []).map(patch),
      }));
      setGridProducts(prev => prev.map(patch));
      setAllProducts(prev => prev.map(patch));
    };
    const channel = supabaseClient
      .channel("marketplace-stock")
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "bloom_products" }, updateStock)
      .subscribe();
    return () => { supabaseClient.removeChannel(channel); };
  }, []);

  // ── Checkout holds — subtract active holds so rows show true availability ────
  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/held-stock?all=1`);
        if (!r.ok || !active) return;
        const j = await r.json();
        if (active) setHeldMap(buildHeldMap(Array.isArray(j.held) ? j.held : []));
      } catch(e) {}
    };
    refresh();
    const stopPoll = setVisibleInterval(refresh, 30_000);
    return () => { active = false; stopPoll(); };
  }, []);

  const allProductsAvail = useMemo(() => applyHolds(allProducts, heldMap), [allProducts, heldMap]);
  // The home page season (festive right now) and its "Seasonal" picks: real, buyable products that suit the occasion.
  const season = useMemo(() => currentSeason(), []);
  const seasonalList = useMemo(() => seasonalProducts(allProductsAvail, season, { isAvailable: (p) => !isSoldOut(p) }), [allProductsAvail, season]);
  const openSeasonal = () => showGrid(`Seasonal · ${season.label}`, seasonalList);
  const newArrivals = useMemo(() => applyHolds(sections.newArrivals || [], heldMap), [sections.newArrivals, heldMap]);
  const popular     = useMemo(() => applyHolds(sections.popular || [], heldMap), [sections.popular, heldMap]);

  const loadGrid = useCallback(async () => {
    setGridLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/marketplace/products?limit=100`);
      const d = r.ok ? await r.json() : { products:[] };
      setGridProducts(d.products || []);
    } catch(e) {}
    setGridLoading(false);
  }, []);

  // showGrid(title?, products?) — if products passed, shows that specific list; else loads full catalogue
  const showGrid = (title, products) => {
    setGridTitle(title || "All products");
    if (products) { setGridOverride(products); setViewMode("grid"); }
    else { setGridOverride(null); setViewMode("grid"); loadGrid(); }
    // The list is its own history entry, so the browser's Back button returns to the home page — not to whatever page was open before it.
    if (!location.state?.grid) navigate(location.pathname + location.search, { state: { grid: true } });
    window.scrollTo({ top:0, behavior:"smooth" });
  };
  // "Browse stores" / "All stores" open the stores gallery — a full panel with its own history entry, like the product lists.
  const openStores = () => {
    setViewMode("stores");
    if (!location.state?.grid) navigate(location.pathname + location.search, { state: { grid: true } });
    window.scrollTo({ top:0, behavior:"smooth" });
  };
  const closeGrid = () => {
    if (location.state?.grid) navigate(-1);                      // pops the entry showGrid pushed; the effect below restores the home view
    else { setViewMode("rows"); setGridOverride(null); }
  };
  // Browser Back / Forward away from the list: leave grid view when its history entry is gone.
  useEffect(() => {
    if (!location.state?.grid && viewMode !== "rows") { setViewMode("rows"); setGridOverride(null); }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cart helpers ──────────────────────────────────────────────────────────�
  const flashToast = (t) => {
    clearTimeout(toastTimer.current);
    setToast(t);
    toastTimer.current = setTimeout(() => setToast(null), 2200);
  };
  const showToast = (product) => {
    const img = Array.isArray(product.images) ? product.images[0] : product.img;
    flashToast({ name: product.name, img });
  };

  const addToCart = (product, variation, qty) => {
    // Store paused (from _storeInfo OR the flat flag on search results) → refuse.
    if (product?._storeInfo?.checkout_paused || product?.checkout_paused) { flashToast({ name: "This store has paused orders", img: null, error: true }); return; }
    const baseStock = variation != null ? (variation.stock ?? 99) : (product.stock ?? 99);
    if (Number(baseStock) === 0) { flashToast({ name: "Out of stock", img: null, error: true }); return; }
    const addQty = Math.min(qty, Number(baseStock));
    // Already holding every unit that's in stock → say so instead of silently doing nothing.
    const inBag = (Array.isArray(cart) ? cart : []).filter(i => i.id === product.id && (i.selectedVariation?.name || null) === (variation?.name || null)).reduce((n, i) => n + i.qty, 0);
    if (inBag >= Number(baseStock)) { flashToast({ name: `Only ${Number(baseStock)} available — already in your bag`, img: null, error: true }); return; }
    // A chosen option carries its OWN price (it replaces the base price) — same rule as the product page and the server.
    const price = variation && Number(variation.price) > 0 ? Number(variation.price) : Number(product.price);
    const item = {
      id:product.id, name:product.name, price,
      images:product.images, img:Array.isArray(product.images)?product.images[0]:null,
      qty:addQty, stock:Number(baseStock),
      variations:product.variations, selectedVariation:variation||null,
      cartKey:`${product.id}-${variation?.name||"default"}-${Date.now()}`,
      user_id:product.user_id, _storeInfo:product._storeInfo,
      ...(product.custom_question && product.custom_answer ? { custom_question: product.custom_question, custom_answer: product.custom_answer } : {}),
      ...(product.custom_image ? { custom_image: product.custom_image } : {}),
    };
    setCart(prev => {
      const idx = prev.findIndex(i => i.id===product.id && (i.selectedVariation?.name||null)===(variation?.name||null) && i._storeInfo?.id===product._storeInfo?.id);
      if (idx >= 0) {
        const max = Number(baseStock);
        return prev.map((x,n) => n===idx?{...x,qty:Math.min(x.qty+addQty,max)}:x);
      }
      return [...prev, item];
    });
    trackAdd(product, { qty: addQty, variation: variation?.name, customer });   // seller's "Left in carts" panel
    showToast(product);
  };

  const onProductClickHandler = (p) => {
    // Sold out → restock request instead of the product page
    if (isSoldOut(p)) setRestockProduct(p);
    else navigate(`/product/${p.id}`);
  };
  // The tile's bag button: products that need a choice open their page; everything else goes straight in the bag.
  // Simple size/option lists are picked in a small sheet right here; anything fancier (colour+size combos, custom question,
  // required upload) opens the full product page; everything else goes straight in the bag.
  const [quickAddProduct, setQuickAddProduct] = useState(null);
  const onQuickAdd = (p) => {
    if (quickChoiceOnly(p)) setQuickAddProduct(p);
    else if (needsChoice(p)) navigate(`/product/${p.id}`);
    else addToCart(p, null, 1);
  };

  // ── Derived content ───────────────────────────────────────────────────────
  const stores = useMemo(() => {
    const seen = new Set(); const list = [];
    for (const p of allProducts) {
      const s = p._storeInfo;
      if (s && !seen.has(s.id)) { seen.add(s.id); list.push({ ...s, _count: 0 }); }
    }
    const byId = new Map(list.map(s => [s.id, s]));
    for (const p of allProducts) { const s = byId.get(p._storeInfo?.id); if (s) s._count++; }
    return list;
  }, [allProducts]);

  const storeProducts = (s) => allProductsAvail.filter(p => p._storeInfo?.id === s.id);
  const storeCover = (s) => { const p = storeProducts(s).find(x => productImage(x)); return p ? productImage(p) : (s.profile_image || null); };
  const featuredStore = useMemo(() => {
    const fromPicks = (sections.staffPicks || []).map(p => p._storeInfo).find(Boolean);
    return stores.find(s => s.id === fromPicks?.id) || [...stores].sort((a, b) => b._count - a._count)[0] || null;
  }, [stores, sections.staffPicks]);
  const likedStores = useMemo(() => stores.filter(s => s.id !== featuredStore?.id).sort((a, b) => b._count - a._count).slice(0, 3), [stores, featuredStore]);
  const moreStores  = useMemo(() => stores.filter(s => s.id !== featuredStore?.id && !likedStores.some(l => l.id === s.id)).slice(0, 8), [stores, featuredStore, likedStores]);
  const promoBar = useMemo(() => buildPromos(allProductsAvail), [allProductsAvail]);
  const under300 = useMemo(() => allProductsAvail.filter(p => Number(p.price) > 0 && Number(p.price) <= 300 && !isSoldOut(p)), [allProductsAvail]);
  const pad = isMobile ? 0 : 40;

  return (
    <div style={{ minHeight:"100vh", background:"var(--offwhite)", color:"var(--ink)", fontFamily:"'Syne', sans-serif" }}>

      {/* ── Top bars (desktop and phone): offers (the same live deals as the tiles below) and categories, both scrollable ── */}
      {(
        <div style={{ background:"var(--white)", borderBottom:"1px solid var(--border)" }}>
          <div style={{ maxWidth:1360, margin:"0 auto", padding:isMobile?"0 12px":"0 40px" }}>
            <ScrollBar isMobile={isMobile} pinnedRight={isMobile ? null : <button onClick={() => navigate("/signup")} style={{ background:"none", border:"none", fontFamily:"inherit", fontSize:14, fontWeight:600, cursor:"pointer", height:44, padding:0, color:"var(--teal)", whiteSpace:"nowrap", flexShrink:0 }}>Sell on Bloom</button>}>
              {[["New arrivals", newArrivals], ["Popular right now", popular]].filter(([, list]) => list && list.length > 1).map(([label, list]) => (
                <button key={label} onClick={() => showGrid(label, list)} style={barLink(true)}>{label}</button>
              ))}
              {promoBar.map(pr => <button key={pr.kind} onClick={() => showGrid(pr.title, pr.items)} style={barLink(true)}>{pr.title}</button>)}
              <button onClick={() => openStores()} style={barLink(true)}>All stores</button>
            </ScrollBar>
          </div>
          <div style={{ borderTop:"1px solid var(--border)" }}>
            <div style={{ maxWidth:1360, margin:"0 auto", padding:isMobile?"0 12px":"0 40px" }}>
              <ScrollBar isMobile={isMobile}>
                {/* Seasonal — first, ahead of the fixed categories; its label follows the season of the year */}
                <SeasonalPill season={season} onClick={openSeasonal} />
                {TAXONOMY.map(c => (
                  <button key={c.slug} onClick={() => navigate(`/category/${c.slug}`)} style={barLink(false)}
                    onMouseEnter={e => e.currentTarget.style.color = "var(--ink)"} onMouseLeave={e => e.currentTarget.style.color = "var(--text2)"}>{c.label}</button>
                ))}
              </ScrollBar>
            </div>
          </div>
        </div>
      )}

      <div style={{ maxWidth:1360, margin:"0 auto", padding:isMobile?"0 0 80px":"36px 40px 100px" }}>
        {viewMode === "rows" && (
          <>
            {/* ── Top module: categories | banner | account cards ── */}
            {isMobile ? (
              <>
                <SectionPanel style={{ padding:"32px 16px 36px", borderRadius:0, borderLeft:"none", borderRight:"none" }}>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4, minmax(0, 1fr))", gap:"26px 12px" }}>
                    {TAXONOMY.slice(0, 8).map(c => (
                      <button key={c.slug} onClick={() => navigate(`/category/${c.slug}`)} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6, background:"none", border:"none", cursor:"pointer", fontFamily:"inherit", padding:0 }}>
                        <CategoryArtTile slug={c.slug} icon={c.icon} style={{ width:"100%", aspectRatio:"1 / 1", maxWidth:72, borderRadius:4 }} />
                        <span style={{ fontSize:12, fontWeight:500, lineHeight:1.2, textAlign:"center" }}>{c.label}</span>
                      </button>
                    ))}
                  </div>
                </SectionPanel>
                <div style={{ padding:"36px 16px 0" }}>
                  <HeroCarousel mobile onStores={() => openStores()} onArrivals={() => showGrid("New arrivals", newArrivals)} onCategory={(slug) => navigate(`/category/${slug}`)} />
                  {cart && cart.length > 0 && <div style={{ marginTop: 36 }}><ContinueCart cart={cart} setCart={setCart} allProducts={allProducts} navigate={navigate} /></div>}
                  <div style={{ marginTop: 36 }}><SeasonBanner mobile season={season} count={seasonalList.length} onOpen={openSeasonal} /></div>
                </div>
              </>
            ) : (
              <div style={{ display:"grid", gridTemplateColumns:"minmax(0, 1fr) 320px", gap:48, alignItems:"stretch" }}>
                <HeroCarousel onStores={() => openStores()} onArrivals={() => showGrid("New arrivals", newArrivals)} onCategory={(slug) => navigate(`/category/${slug}`)} />
                <aside style={{ display:"flex", flexDirection:"column", gap:28 }}>
                  <ContinueCart cart={cart} setCart={setCart} allProducts={allProducts} navigate={navigate} />
                  <div style={{ background:"var(--white)", border:"1px solid var(--border)", borderRadius:4, padding:26 }}>
                    {customer ? (
                      <>
                        <div className="serif" style={{ fontSize:28, lineHeight:.95 }}>Hello, {(customer.name || "").split(" ")[0] || "there"}</div>
                        <p style={{ margin:"12px 0 0", fontSize:14, lineHeight:1.5, color:"var(--text2)" }}>Track your orders and pick up where you left off.</p>
                        <button className="btn-pink" onClick={() => navigate("/customer/orders")} style={{ width:"100%", marginTop:20, height:44, padding:0, fontSize:14 }}>Your orders</button>
                      </>
                    ) : (
                      <>
                        <div className="serif" style={{ fontSize:28, lineHeight:.95 }}>Sign in for faster checkout</div>
                        <p style={{ margin:"12px 0 0", fontSize:14, lineHeight:1.5, color:"var(--text2)" }}>Track orders and save your wishlist.</p>
                        <button className="btn-pink" onClick={() => navigate("/customer/login")} style={{ width:"100%", marginTop:20, height:44, padding:0, fontSize:14 }}>Sign in</button>
                      </>
                    )}
                  </div>
                  <div style={{ background:"var(--white)", border:"1px solid var(--border)", borderRadius:4, padding:26, flex:1, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
                    <div><div className="serif" style={{ fontSize:28, lineHeight:.95 }}>Sell on Bloom</div><p style={{ margin:"12px 0 0", fontSize:14, lineHeight:1.5, color:"var(--text2)" }}>Open a store free and keep 100% of your sales.</p></div>
                    <button className="btn-ghost" onClick={() => navigate("/signup")} style={{ width:"100%", marginTop:20, height:44, padding:0, fontSize:14, borderColor:"var(--ink)" }}>Start selling</button>
                  </div>
                </aside>
              </div>
            )}

            {/* ── Season banner: opens the Seasonal picks (desktop; phones get it right under the main banner) ── */}
            {!isMobile && <div style={{ marginTop:64 }}><SeasonBanner season={season} count={seasonalList.length} onOpen={openSeasonal} /></div>}

            {/* ── Trust strip ── */}
            <div style={{ marginTop:isMobile?40:64, display:"grid", gridTemplateColumns:isMobile?"repeat(2, minmax(0, 1fr))":"repeat(4, minmax(0, 1fr))", background:"var(--white)", border:"1px solid var(--border)", borderRadius:isMobile?0:4, borderLeft:isMobile?"none":undefined, borderRight:isMobile?"none":undefined }}>
              {[["privacy","Secure UPI and card payments"],["bag","One cart for every store"],["truck","Email when your order ships"],["star","Reviews from real buyers"]].map(([ic, t], i) => (
                <div key={t} style={{ display:"flex", gap:12, alignItems:"center", padding:isMobile?"22px 16px":"26px 30px", borderRight:i < 3 ? "1px solid var(--border)" : "none", borderBottom:isMobile && i < 2 ? "1px solid var(--border)" : "none", fontSize:isMobile?13:14, fontWeight:600, lineHeight:1.25 }}>
                  <span style={{ color:"var(--teal)", display:"flex", flexShrink:0 }}><Icon n={ic} size={isMobile?22:24} /></span>{t}
                </div>
              ))}
            </div>

            {!loading && <div style={{ marginTop:isMobile?40:64 }}><PromoTiles products={allProductsAvail} isMobile={isMobile} onOpen={(title, list) => showGrid(title, list)} /></div>}

            {!isMobile && !loading && <div style={{ marginTop:64 }}><CategoryTiles allProducts={allProducts} navigate={navigate} isMobile={false} /></div>}

            {loading ? (
              <div style={{ textAlign:"center", padding:"80px 20px" }}>
                <div style={{ width:32, height:32, border:"3px solid var(--border)", borderTopColor:"var(--teal)", borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto 14px" }} />
                <p style={{ color:"var(--ink-soft)", fontSize:14 }}>Loading…</p>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:isMobile?40:64, marginTop:isMobile?40:64 }}>

                <Shelf title="New arrivals" products={newArrivals} isMobile={isMobile} onSeeAll={() => showGrid("New arrivals", newArrivals)} onOpen={onProductClickHandler} onAdd={onQuickAdd} addedIds={addedIds} />

                {/* ── Store of the week ── */}
                {featuredStore && (
                  <SectionPanel style={{ overflow:"hidden", borderRadius:isMobile?0:4, borderLeft:isMobile?"none":undefined, borderRight:isMobile?"none":undefined, display:isMobile?"block":"grid", gridTemplateColumns:"7fr 5fr" }}>
                    <div style={{ minHeight:isMobile?0:400, aspectRatio:isMobile?"4 / 3":undefined, background:"var(--surface)", position:"relative" }}>
                      {storeCover(featuredStore) && <img src={thumbUrl(storeCover(featuredStore), 900)} alt={featuredStore.store_name} loading="lazy" style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover" }} />}
                    </div>
                    <div style={{ padding:isMobile?"16px 12px 20px":"48px", display:"flex", flexDirection:"column", justifyContent:"center" }}>
                      <div style={{ fontSize:14, fontWeight:600, color:"var(--teal)" }}>Store of the week</div>
                      <h2 style={{ margin:"10px 0 0", fontFamily:"'Instrument Serif', Georgia, serif", fontSize:isMobile?44:62, lineHeight:1, fontWeight:400, letterSpacing:"-0.02em" }}>{featuredStore.store_name}</h2>
                      <p style={{ margin:"16px 0 0", fontSize:isMobile?15:16, lineHeight:1.6, color:"var(--text2)" }}>{featuredStore._count} {featuredStore._count === 1 ? "product" : "products"} on Bloom, shipped by the seller.</p>
                      <div style={{ display:"flex", gap:12, marginTop:24, flexWrap:"wrap" }}>
                        <button className="btn-ghost" onClick={() => navigate(`/store/${featuredStore.store_slug}`)} style={{ height:46, padding:"0 22px", fontSize:15, borderColor:"var(--ink)" }}>Visit the store</button>
                      </div>
                    </div>
                  </SectionPanel>
                )}

                <Shelf title="Popular right now" products={popular} isMobile={isMobile} onSeeAll={() => showGrid("Popular right now", popular)} onOpen={onProductClickHandler} onAdd={onQuickAdd} addedIds={addedIds} />
                <Shelf title="Under ₹300" products={under300} isMobile={isMobile} onSeeAll={() => showGrid("Under ₹300", under300)} onOpen={onProductClickHandler} onAdd={onQuickAdd} addedIds={addedIds} />

                {/* ── Stores we like ── */}
                {likedStores.length > 0 && (
                  <SectionPanel style={{ padding:isMobile?"20px 0 22px":"28px 24px", borderRadius:isMobile?0:4, borderLeft:isMobile?"none":undefined, borderRight:isMobile?"none":undefined }}>
                    <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", margin:isMobile?"0 12px 14px":"0 0 20px" }}>
                      <h2 style={{ margin:0, fontFamily:"'Instrument Serif', Georgia, serif", fontSize:isMobile?38:52, fontWeight:400, letterSpacing:"-0.02em" }}>Stores we like</h2>
                      <SeeAll onClick={() => openStores()} label="All stores" />
                    </div>
                    <div style={isMobile ? { display:"flex", gap:12, overflowX:"auto", padding:"0 12px", scrollbarWidth:"none" } : { display:"grid", gridTemplateColumns:"repeat(3, minmax(0, 1fr))", gap:16 }}>
                      {likedStores.map(s => (
                        <button key={s.id} onClick={() => navigate(`/store/${s.store_slug}`)} style={{ width:isMobile?240:"auto", flex:isMobile?"none":undefined, textAlign:"left", background:"none", border:"none", padding:0, cursor:"pointer", fontFamily:"inherit", color:"var(--ink)" }}>
                          <div style={{ aspectRatio:"5 / 4", borderRadius:4, background:"var(--surface)", overflow:"hidden" }}>
                            {storeCover(s) && <img src={thumbUrl(storeCover(s), 700)} alt={s.store_name} loading="lazy" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />}
                          </div>
                          <div style={{ paddingTop:12, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                            <div><div style={{ fontFamily:"'Instrument Serif', Georgia, serif", fontSize:isMobile?28:32, lineHeight:1.05 }}>{s.store_name}</div><div style={{ fontSize:14, color:"var(--ink-soft)", marginTop:2 }}>{s._count} {s._count === 1 ? "product" : "products"}</div></div>
                            {!isMobile && <Icon n="forward" size={20} />}
                          </div>
                        </button>
                      ))}
                    </div>
                    {moreStores.length > 0 && (
                      <div style={{ margin:isMobile?"20px 12px 0":"24px 0 0", paddingTop:20, borderTop:"1px solid var(--border)", display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
                        <span style={{ fontSize:13, fontWeight:600, color:"var(--ink-soft)", marginRight:6 }}>More stores</span>
                        {moreStores.map(s => (
                          <button key={s.id} onClick={() => navigate(`/store/${s.store_slug}`)} style={{ display:"inline-flex", alignItems:"center", gap:8, height:36, padding:"0 12px 0 6px", border:"1px solid var(--border)", borderRadius:4, background:"var(--white)", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit", color:"var(--ink)" }}>
                            {s.profile_image
                              ? <img src={thumbUrl(s.profile_image, 60)} alt="" style={{ width:24, height:24, borderRadius:2, objectFit:"cover" }} />
                              : <span style={{ width:24, height:24, borderRadius:2, background:"var(--surface)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700 }}>{(s.store_name || "?").slice(0, 1).toUpperCase()}</span>}
                            {s.store_name}
                          </button>
                        ))}
                      </div>
                    )}
                  </SectionPanel>
                )}

                {allProducts.length === 0 && (
                  <div style={{ textAlign:"center", padding:"60px 20px", color:"var(--ink-soft)" }}>
                    <Icon n="package" size={40} />
                    <p style={{ fontSize:15, marginTop:12 }}>Products coming soon.</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ── STORES GALLERY ── */}
        {viewMode === "stores" && <StoresGallery stores={stores} productsOf={storeProducts} onBack={closeGrid} navigate={navigate} isMobile={isMobile} />}

        {/* ── GRID (View all / filtered) ── */}
        {viewMode === "grid" && (() => {
          const displayProducts = gridOverride || gridProducts;
          const isLoading = !gridOverride && gridLoading;
          return (
            <div style={{ padding:isMobile?"16px 12px 0":0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
                <button onClick={closeGrid} className="btn-ghost" style={{ height:40, padding:"0 14px", fontSize:14, display:"inline-flex", alignItems:"center", gap:6 }}><Icon n="back" size={16} />Back</button>
                <h1 className="serif" style={{ fontSize:isMobile?36:52, margin:0 }}>{gridTitle}</h1>
              </div>
              {isLoading ? (
                <div style={{ textAlign:"center", padding:"60px 0" }}><div style={{ width:32, height:32, border:"3px solid var(--border)", borderTopColor:"var(--teal)", borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto" }} /></div>
              ) : (
                <div style={{ display:"grid", gridTemplateColumns:isMobile?"repeat(2, minmax(0, 1fr))":"repeat(5, minmax(0, 1fr))", gap:isMobile?10:14 }}>
                  {displayProducts.map(p => <ProductTile key={p.id} product={p} onOpen={onProductClickHandler} onAdd={onQuickAdd} added={addedIds.has(p.id)} />)}
                  {displayProducts.length === 0 && <div style={{ gridColumn:"1 / -1", textAlign:"center", padding:"60px 0", color:"var(--ink-soft)" }}>No products found.</div>}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* ── SELL ON BLOOM ── */}
      <div style={{ background:"var(--teal-light)", borderTop:"1px solid var(--teal-mid)", borderBottom:"1px solid var(--teal-mid)" }}>
        <div style={{ maxWidth:1360, margin:"0 auto", padding:isMobile?"28px 16px":"48px 40px", display:"flex", flexWrap:"wrap", alignItems:isMobile?"stretch":"center", justifyContent:"space-between", gap:isMobile?16:"24px 40px", flexDirection:isMobile?"column":"row" }}>
          <div>
            <h2 className="serif" style={{ fontSize:isMobile?40:60, margin:0 }}>Sell on Bloom</h2>
            <p style={{ fontSize:isMobile?15:17, color:"var(--text2)", lineHeight:1.5, margin:"8px 0 0", maxWidth:620 }}>Open your store free, take UPI and card payments, and keep 100% of your sales. No KYC to start, any UPI app.</p>
          </div>
          <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
            <button className="btn-pink" onClick={() => navigate("/signup")} style={{ height:isMobile?48:52, padding:"0 28px", fontSize:16 }}>Open your store free</button>
            {goToDemo && <button className="btn-ghost" onClick={goToDemo} style={{ height:isMobile?48:52, padding:"0 24px", fontSize:15, borderColor:"var(--ink)" }}>See a live store</button>}
          </div>
          <div style={{ flexBasis:"100%", display:"grid", gridTemplateColumns:isMobile?"repeat(2, minmax(0, 1fr))":"repeat(4, minmax(0, 1fr))", gap:isMobile?10:16 }}>
            {[["store","Create your store","Sign up, add products, done."],["wallet","Add your UPI ID","GPay, PhonePe, Paytm — any UPI."],["link","Share your link","Send customers your link or QR."],["rupee-circle","Get paid instantly","Money goes straight to you."]].map(([ic, t, d]) => (
              <div key={t} style={{ background:"var(--white)", border:"1px solid var(--border)", borderRadius:4, padding:"14px 16px", display:"flex", gap:12, alignItems:"flex-start" }}>
                <span style={{ width:34, height:34, borderRadius:4, background:"var(--teal-light)", color:"var(--teal)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><Icon n={ic} size={18} /></span>
                <div style={{ minWidth:0 }}><div style={{ fontSize:14, fontWeight:700 }}>{t}</div><div style={{ fontSize:13, color:"var(--text2)", lineHeight:1.45, marginTop:2 }}>{d}</div></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer/>


      <QuickAddSheet product={quickAddProduct} onClose={() => setQuickAddProduct(null)}
        onAdd={(variation, qty) => addToCart(quickAddProduct, variation, qty)}
        onOpenPage={() => { const id = quickAddProduct?.id; setQuickAddProduct(null); if (id) navigate(`/product/${id}`); }} />

      {/* ── Add-to-cart toast ── */}
      <div style={{ position:"fixed", bottom:isMobile?76:28, left:"50%", transform:`translateX(-50%) translateY(${toast ? 0 : 80}px)`, zIndex:9999, transition:"transform 0.2s ease-out, opacity 0.2s", opacity:toast ? 1 : 0, pointerEvents:"none" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, background:"var(--ink)", borderRadius:4, padding:"10px 18px 10px 12px", boxShadow:"0 12px 32px rgba(16,24,40,0.16)", minWidth:220, color:"#fff" }}>
          {toast?.img
            ? <img src={thumbUrl(toast.img, 100)} alt="" style={{ width:36, height:36, borderRadius:2, objectFit:"cover", flexShrink:0 }} />
            : <span style={{ width:36, height:36, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}><Icon n={toast?.error ? "warning" : "success"} size={22} color={toast?.error ? "#FEC84B" : "#47CD89"} /></span>}
          <div>
            <div style={{ fontSize:12, fontWeight:600, color:"#D8D1C7" }}>{toast?.error ? "Can't add" : "Added to your bag"}</div>
            <div style={{ fontSize:14, fontWeight:600, maxWidth:220, overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis" }}>{toast?.name}</div>
          </div>
        </div>
      </div>

      {/* ── Restock Request Modal ── */}
      {restockProduct && (
        <RestockRequestModal product={restockProduct} storeId={restockProduct.user_id} customer={customer} onClose={() => setRestockProduct(null)} onSuccess={() => {}} />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
